// src/components/onboarding/TabbedSessionManagement.tsx - COMPLETE IMPLEMENTATION
// ✅ FIXED: Full session management with tabs, loading, actions, and status display

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Clock,
  AlertTriangle,
  RefreshCw,
  User,
  Calendar,
  Package,
  CheckCircle,
  Play,
  UserPlus,
  Badge,
  ArrowRight,
  Trash2,
  Package2,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { onboardingAPI } from '../../services/onboardingAPI';
import { OnboardingSession, RoleTemplate } from '../../types/onboarding';
import { User as UserType } from '../../types/user';

// ✅ TYPES: Session summary interface
interface OnboardingSessionSummary {
  sessionId: string;
  officerName: string;
  badgeNumber: string;
  roleTemplateName: string;
  status: 'Draft' | 'Active' | 'Pending' | 'Ready' | 'Partial' | 'Complete' | 'Completed';
  progress: {
    categoriesComplete: number;
    categoriesTotal: number;
    itemsPending: number;
    percentComplete: number;
  };
  startDate: string;
  lastUpdated: string;
  createdBy: string;
}

// ✅ HELPER: Status configuration
type StatusType = 'Draft' | 'Active' | 'Pending' | 'Ready' | 'Partial' | 'Complete' | 'Completed';

const getStatusColor = (status: StatusType): string => {
  const colors: Record<StatusType, string> = {
    'Draft': 'gray',
    'Active': 'blue',
    'Pending': 'yellow',
    'Ready': 'green',
    'Partial': 'orange',
    'Complete': 'green',
    'Completed': 'green'
  };
  return colors[status] || 'gray';
};

const getStatusIcon = (status: StatusType) => {
  switch (status) {
    case 'Draft': return Clock;
    case 'Active': return Play;
    case 'Pending': return AlertTriangle;
    case 'Ready': return CheckCircle;
    case 'Partial': return AlertTriangle;
    case 'Complete': return CheckCircle2;
    case 'Completed': return CheckCircle2;
    default: return Clock;
  }
};

// ✅ HELPER: Convert OnboardingSession to summary
const sessionToSummary = (session: OnboardingSession): OnboardingSessionSummary => {
  return {
    sessionId: session.sessionId,
    officerName: session.officerName,
    badgeNumber: session.badgeNumber,
    roleTemplateName: session.roleTemplateName,
    status: session.status as StatusType,
    progress: {
      categoriesComplete: session.progress?.categoriesComplete || 0,
      categoriesTotal: session.progress?.categoriesTotal || 0,
      itemsPending: session.progress?.itemsPending || 0,
      percentComplete: session.progress?.percentComplete || 0
    },
    startDate: session.startDate,
    lastUpdated: session.updatedAt || session.createdAt || new Date().toISOString(),
    createdBy: session.createdBy || 'Unknown'
  };
};

// ✅ COMPONENT: Quick Start Form
interface QuickStartFormProps {
  officers: UserType[];
  roleTemplates: RoleTemplate[];
  onQuickStart: (quickStartData: any) => void;
  creatingSession: boolean;
}

const QuickStartForm: React.FC<QuickStartFormProps> = ({
  officers,
  roleTemplates,
  onQuickStart,
  creatingSession
}) => {
  const [quickStart, setQuickStart] = useState({
    officerId: '',
    roleTemplateId: '',
    startDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const handleSubmit = () => {
    if (!quickStart.officerId || !quickStart.roleTemplateId) {
      return;
    }
    onQuickStart(quickStart);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
        <UserPlus className="w-5 h-5 mr-2 text-blue-600" />
        Quick Start New Session
      </h2>
      
      <div className="space-y-4">
        {/* Officer Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Officer
          </label>
          <select
            value={quickStart.officerId}
            onChange={(e) => setQuickStart(prev => ({ ...prev, officerId: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Choose an officer...</option>
            {officers.map(officer => (
              <option key={officer.userId} value={officer.userId}>
                {officer.firstName} {officer.lastName} - Badge #{officer.badgeNumber}
              </option>
            ))}
          </select>
        </div>

        {/* Role Template Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Role Template
          </label>
          <select
            value={quickStart.roleTemplateId}
            onChange={(e) => setQuickStart(prev => ({ ...prev, roleTemplateId: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Choose a role...</option>
            {roleTemplates.map(template => (
              <option key={template.templateId} value={template.templateId}>
                {template.roleName} ({template.categories?.length || 0} categories)
              </option>
            ))}
          </select>
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={quickStart.startDate}
            onChange={(e) => setQuickStart(prev => ({ ...prev, startDate: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={quickStart.notes}
            onChange={(e) => setQuickStart(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Special requirements, notes..."
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Start Button */}
        <button
          onClick={handleSubmit}
          disabled={!quickStart.officerId || !quickStart.roleTemplateId || creatingSession}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {creatingSession ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Creating Session...</span>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span>Start Onboarding Session</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ✅ COMPONENT: Session Card
interface SessionCardProps {
  session: OnboardingSessionSummary;
  onResume: (sessionId: string) => void;
  onDelete: (sessionId: string, officerName: string) => void;
  onViewAssignments?: (sessionId: string) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({ 
  session, 
  onResume, 
  onDelete, 
  onViewAssignments 
}) => {
  const StatusIcon = getStatusIcon(session.status);
  const statusColor = getStatusColor(session.status);
  const isCompleted = ['Complete', 'Completed'].includes(session.status);
  const isPartial = session.status === 'Partial';

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors relative">
      {/* Delete Button - Upper Right Corner */}
      <button
        onClick={() => onDelete(session.sessionId, session.officerName)}
        className="absolute top-3 right-3 text-gray-400 hover:text-red-600 transition-colors p-1"
        title={`Delete session for Officer ${session.officerName}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>

      <div className="flex items-center justify-between pr-8">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <StatusIcon className={`w-5 h-5 text-${statusColor}-600`} />
            <h3 className="text-lg font-semibold text-gray-900">
              Officer {session.officerName}
            </h3>
            <span className={`px-2 py-1 rounded text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800`}>
              {session.status}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
            <div className="flex items-center space-x-1">
              <Badge className="w-4 h-4" />
              <span>Badge #{session.badgeNumber}</span>
            </div>
            <div className="flex items-center space-x-1">
              <User className="w-4 h-4" />
              <span>{session.roleTemplateName}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>{new Date(session.startDate).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>Updated {new Date(session.lastUpdated).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">
                Progress: {session.progress.categoriesComplete} of {session.progress.categoriesTotal} categories
              </span>
              <span className="text-sm text-gray-500">
                {session.progress.percentComplete}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`bg-${statusColor}-600 h-2 rounded-full transition-all duration-300`}
                style={{ width: `${session.progress.percentComplete}%` }}
              />
            </div>
          </div>

          {/* Partial Session Alert */}
          {isPartial && (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-2 mb-3">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-800">
                  Partial completion - some categories still need items
                </span>
              </div>
            </div>
          )}

          {/* Pending Items Alert */}
          {session.progress.itemsPending > 0 && !isPartial && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 mb-3">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  {session.progress.itemsPending} items pending delivery
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2 ml-4">
          {isCompleted ? (
            <button
              onClick={() => onViewAssignments?.(session.sessionId)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>View Assignments</span>
            </button>
          ) : isPartial ? (
            <button
              onClick={() => onResume(session.sessionId)}
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center space-x-2"
            >
              <ArrowRight className="w-4 h-4" />
              <span>Finish Remaining</span>
            </button>
          ) : (
            <button
              onClick={() => onResume(session.sessionId)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <ArrowRight className="w-4 h-4" />
              <span>{session.status === 'Draft' ? 'Continue' : 'Resume'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ✅ COMPONENT: Session List
interface SessionListProps {
  sessions: OnboardingSessionSummary[];
  loading: boolean;
  emptyMessage: string;
  emptyIcon: React.ComponentType<any>;
  onRefresh: () => void;
  onResume: (sessionId: string) => void;
  onDelete: (sessionId: string, officerName: string) => void;
  onViewAssignments?: (sessionId: string) => void;
}

const SessionList: React.FC<SessionListProps> = ({
  sessions,
  loading,
  emptyMessage,
  emptyIcon: EmptyIcon,
  onRefresh,
  onResume,
  onDelete,
  onViewAssignments
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading sessions...</span>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12">
        <EmptyIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Sessions Found</h3>
        <p className="text-gray-600 mb-6">{emptyMessage}</p>
        <button
          onClick={onRefresh}
          className="text-blue-600 hover:text-blue-700 text-sm flex items-center space-x-1 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sessions.map(session => (
        <SessionCard
          key={session.sessionId}
          session={session}
          onResume={onResume}
          onDelete={onDelete}
          onViewAssignments={onViewAssignments}
        />
      ))}
    </div>
  );
};

// ✅ MAIN COMPONENT: TabbedSessionManagement
interface TabbedSessionManagementProps {
  officers: UserType[];
  roleTemplates: RoleTemplate[];
  onQuickStart: (quickStartData: any) => void;
  creatingSession: boolean;
}

const TabbedSessionManagement: React.FC<TabbedSessionManagementProps> = ({
  officers,
  roleTemplates,
  onQuickStart,
  creatingSession
}) => {
  const navigate = useNavigate();
  
  // ✅ SESSION STATE
  const [activeSessions, setActiveSessions] = useState<OnboardingSessionSummary[]>([]);
  const [completedSessions, setCompletedSessions] = useState<OnboardingSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  
  // ✅ DELETE STATE
  const [sessionToDelete, setSessionToDelete] = useState<{sessionId: string, officerName: string} | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);

  // ✅ LOAD SESSIONS
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      console.log('🔍 Loading onboarding sessions...');
      
      // Get all sessions (we'll filter them)
      const response = await onboardingAPI.getActiveSessions();
      
      if (response.success && response.data) {
        const allSessions = response.data.sessions || [];
        console.log(`📊 Loaded ${allSessions.length} total sessions`);
        
        // Convert to summary format
        const sessionSummaries = allSessions.map(sessionToSummary);
        
        // ✅ FIXED: Include 'Partial' in active sessions so they can be resumed
        const activeStatuses = ['Draft', 'Active', 'Pending', 'Ready', 'Partial'];
        const completedStatuses = ['Complete', 'Completed'];
        
        const active = sessionSummaries.filter((s: OnboardingSessionSummary) => activeStatuses.includes(s.status));
        const completed = sessionSummaries.filter((s: OnboardingSessionSummary) => completedStatuses.includes(s.status));
        
        console.log(`📊 Sessions breakdown: ${active.length} active, ${completed.length} completed`);
        
        setActiveSessions(active);
        setCompletedSessions(completed);
      } else {
        console.error('❌ Failed to load sessions:', response.error);
        setActiveSessions([]);
        setCompletedSessions([]);
      }
    } catch (err) {
      console.error('❌ Error loading sessions:', err);
      setActiveSessions([]);
      setCompletedSessions([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ SESSION ACTIONS
  const handleResumeSession = (sessionId: string) => {
    console.log(`🔄 Resuming session: ${sessionId}`);
    navigate(`/onboarding/${sessionId}`);
  };

  const handleViewAssignments = (sessionId: string) => {
    console.log(`👀 Viewing assignments for session: ${sessionId}`);
    // Navigate to assignments filtered by session
    navigate(`/assignments?sessionId=${sessionId}`);
  };

  const handleDeleteRequest = (sessionId: string, officerName: string) => {
    setSessionToDelete({ sessionId, officerName });
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    setDeletingSession(true);
    try {
      console.log(`🗑️ Deleting session: ${sessionToDelete.sessionId}`);
      
      const response = await onboardingAPI.deleteSession(sessionToDelete.sessionId);
      
      if (response.success) {
        // Remove from both lists
        setActiveSessions(prev => prev.filter(s => s.sessionId !== sessionToDelete.sessionId));
        setCompletedSessions(prev => prev.filter(s => s.sessionId !== sessionToDelete.sessionId));
        
        console.log(`✅ Session deleted successfully: ${sessionToDelete.officerName}`);
        setSessionToDelete(null);
      } else {
        throw new Error(response.error?.message || 'Failed to delete session');
      }
    } catch (err) {
      console.error('❌ Error deleting session:', err);
    } finally {
      setDeletingSession(false);
    }
  };

  const handleQuickStartWrapper = async (quickStartData: any) => {
    // Call the parent handler
    await onQuickStart(quickStartData);
    // Refresh sessions after creation
    setTimeout(() => {
      loadSessions();
    }, 1000);
  };

  // ✅ TAB COUNTS
  const activeCount = activeSessions.length;
  const completedCount = completedSessions.length;

  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Quick Start Section */}
        <div className="xl:col-span-1">
          <QuickStartForm
            officers={officers}
            roleTemplates={roleTemplates}
            onQuickStart={handleQuickStartWrapper}
            creatingSession={creatingSession}
          />
        </div>

        {/* Sessions Section with Tabs */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border">
            {/* Tab Headers */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'active'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Active Sessions
                  {activeCount > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      activeTab === 'active' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      {activeCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('completed')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'completed'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Completed Sessions
                  {completedCount > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      activeTab === 'completed' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      {completedCount}
                    </span>
                  )}
                </button>
              </nav>
              
              {/* Refresh Button */}
              <div className="absolute top-4 right-6">
                <button
                  onClick={loadSessions}
                  disabled={loading}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6 relative">
              {activeTab === 'active' ? (
                <SessionList
                  sessions={activeSessions}
                  loading={loading}
                  emptyMessage="Start a new onboarding session to get started."
                  emptyIcon={Package}
                  onRefresh={loadSessions}
                  onResume={handleResumeSession}
                  onDelete={handleDeleteRequest}
                />
              ) : (
                <SessionList
                  sessions={completedSessions}
                  loading={loading}
                  emptyMessage="Completed sessions will appear here after equipment collection."
                  emptyIcon={CheckCircle2}
                  onRefresh={loadSessions}
                  onResume={handleResumeSession}
                  onDelete={handleDeleteRequest}
                  onViewAssignments={handleViewAssignments}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {sessionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Onboarding Session</h3>
            </div>
            
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete the onboarding session for:
            </p>
            <p className="font-semibold text-gray-900 mb-4">
              Officer {sessionToDelete.officerName}
            </p>
            
            <p className="text-sm text-gray-600 mb-6">
              This will return any reserved inventory items to available stock and cannot be undone.
            </p>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setSessionToDelete(null)}
                disabled={deletingSession}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSession}
                disabled={deletingSession}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deletingSession ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Session</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TabbedSessionManagement;
